import { useEffect, useRef } from 'react';
import Label from '../../../../ui/label';
import LevelRanking from './category-type/level-ranking';
import BySalaryRank from './category-type/by-salary-rank';
import UniformCoverage from './category-type/uniform-coverage';
import { useDispatch } from 'react-redux';
import ClientInputBasicPlans from './client-input-basic-plans';
import ClientInputCoverageType from './client-input-coverage-type';
import { resetLevelRanking } from '../../../../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.slice';
import { resetUniformCoverage } from '../../../../../features/gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.slice';
import { resetSalaryRank } from '../../../../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.slice';
import { resetSelectedRiders } from '../../../../../features/gms-proposal-forms/attachable-rider/attachable-riders.slice';
import AttachableRiders from './riders/attachable-riders';
import ClientInputAmountLoans from './client-input-amount-loans';
import InputsAmountOfLoans from './inputs-amount-of-loans';
import ClientInputTypeOfProposal from './client-input-type-of-proposal';
import ClientInputLoanMaturity from './client-input-loan-maturity';
import ClientInputForBorrowers from './age-borrowers/client-input-for-borrowers';
import ClientInputCommissionAndService from './client-input-commission-and-service';
import ClientInputFileUpload from './client-input-file-upload';
import ClientInputProductTypes from './client-input-product-types';
import ClientInputPrototype from './client-input-prototype';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import ClientInputPaymentTerm from './client-input-payment-term';

export default function ClientInputGroupInsurancePlansAndRiders() {

    const dispatch = useDispatch();
    const {
        isCustomize,
        isPrototype,
        isLevelRanking,
        isUniformCoverage,
        isSalaryRank,
        showAmountOfLoansInput,
        isShowAttachableRiders,
        isShowPaymentTermAndLoanMaturity,
        isShowAmountOfLoans,
        isShowCoverageType,
        isShowRates,
        isShowAttachedFile,
        isProductGPA, 
        isProductGCLI, 
        isProductGYRT, 
        isProductGCI,
        coverage,
        borrowerAgeOptionsList,
        minAgeProfile,
        maxAgeProfile,
    } = useProposalTypeData();
    const prevCoverageTypeRef = useRef<number | null>(null);

    useEffect(() => {
        const currentId = coverage?.id ?? null;

        if ( prevCoverageTypeRef.current !== null && prevCoverageTypeRef.current !== currentId ) {
            dispatch(resetLevelRanking());
            dispatch(resetUniformCoverage());
            dispatch(resetSalaryRank());
            dispatch(resetSelectedRiders());
        }
        prevCoverageTypeRef.current = currentId;
    }, [coverage, dispatch]);

    const isDefault = !isCustomize && !isPrototype;

    return (
        <div className="space-y-4">
            {/* TYPE OF PROPOSAL / PRODUCT / BASIC PLAN / PROTOTYPE */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <ClientInputTypeOfProposal />
                {isDefault && (
                    <>
                        <div className="hidden lg:block col-span-1 space-y-3 rounded bg-white p-3.5 md:p-4 border border-gray-200" />
                        <div className="hidden lg:block col-span-1 space-y-3 rounded bg-white p-3.5 md:p-4 border border-gray-200" />
                    </>
                )}
                {isCustomize && (
                    <div className="space-y-3 rounded bg-white p-3.5 md:p-4 border border-gray-200">
                        <ClientInputProductTypes />
                    </div>
                )}
                {isCustomize && (
                    <div className="space-y-3 rounded bg-white p-3.5 md:p-4 border border-gray-200">
                        <ClientInputBasicPlans />
                    </div>
                )}
                {isPrototype && (
                    <div className="md:col-span-2 lg:col-span-2 space-y-3 rounded bg-white p-3.5 md:p-4 border border-gray-200">
                        <ClientInputPrototype />
                    </div>
                )}
            </div>
            {/* COVERAGE TYPE */}
            {isCustomize && isShowCoverageType && (
                <div className='rounded bg-white border border-gray-200'>
                    <div className="flex flex-col gap-3 border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="min-w-0 flex-1">
                            <Label
                                label='Coverage Type'
                                fontWeight="font-semibold text-gray-800"
                                fontSize="text-xs"
                                requiredText=' *'
                                isRequired={true}
                            />
                            <p className="mt-1 text-[11px] leading-4 text-gray-500 sm:text-xs">
                                Select the coverage type and configure the corresponding
                                coverage details for the proposal.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3 p-3.5 md:p-4">
                        <ClientInputCoverageType />
                        {isLevelRanking && <LevelRanking />}
                        {isUniformCoverage && <UniformCoverage />}
                        {isSalaryRank && <BySalaryRank />}
                        {isShowAttachedFile && <ClientInputFileUpload isRequired={isSalaryRank} />}
                    </div>
                </div>
            )}
            {/* AMOUNT OF LOANS */}
            {isCustomize && isShowAmountOfLoans && (
                <div className='rounded bg-white border border-gray-200'>
                    <div className="flex flex-col gap-3 border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="min-w-0 flex-1">
                            <Label
                                label='Amount of Loans'
                                fontWeight="font-semibold text-gray-800"
                                fontSize="text-xs"
                                requiredText=' *'
                                isRequired={true}
                            />
                            <p className="mt-1 text-[11px] leading-4 text-gray-500 sm:text-xs">
                                Select how the loan amount will be determined and provide
                                the required loan amount details.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3 p-3.5 md:p-4">
                        <ClientInputAmountLoans />
                        {showAmountOfLoansInput && (<InputsAmountOfLoans />)}
                    </div>
                </div>
            )}
            {/* ATTACHABLE RIDERS */}
            {isCustomize && isShowAttachableRiders && !isProductGCI && (
                <div className="space-y-2">
                    <AttachableRiders />
                </div>
            )}
            {/* RATES */}
            {isCustomize && isShowRates && (
                <div className="space-y-3">
                    {/* PAYMENT TERM / LOAN MATURITY */}
                    {isShowPaymentTermAndLoanMaturity && (
                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_1fr]">
                            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                <div className="">
                                    <ClientInputPaymentTerm />
                                </div>
                            </div>
                            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                <div className="">
                                    <ClientInputLoanMaturity />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* BORROWERS / COMMISSION */}
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.4fr_1fr]">
                        {/* BORROWERS */}
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                            <ClientInputForBorrowers 
                                isProductGPA={isProductGPA}
                                isProductGCLI={isProductGCLI}
                                isProductGYRT={isProductGYRT}
                                isProductGCI={isProductGCI}
                                borrowerAgeOptionsList={borrowerAgeOptionsList}
                                minAge={minAgeProfile}
                                maxAge={maxAgeProfile}
                                isPreview={false} 
                            />
                        </div>
                        {/* COMMISSION */}
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                            <ClientInputCommissionAndService />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}